import { useEffect } from "react";
import Link from "next/link";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import useProduct from "@/hooks/useProduct";
import { IFontProps, TResultSheet, TApplicationData } from "@/typings/Props";
import { theme } from "@/styles/theme";
import { media } from "@/styles/media";
import Header from "@/components/main/Header";
import Banner from "@/components/learn/Banner";
import Timeline from "@/components/learn/Timeline";
import ResultSheet from "@/components/products/detail/ResultSheet";
import Footer from "@/components/main/Footer";

export default function Home() {
  const { t } = useTranslation();
  const product = useProduct();

  useEffect(() => {
    product.fetchProductsAsync();
  }, []);

  useEffect(() => {
    console.log(product.state.products);
  }, [product.state.products]);

  if (product.state.loading) {
    return <>loading...</>;
  }

  const RevolutionDatas: TApplicationData[] = [
    {
      id: 1,
      title: t("menu.learn.revolutionizing.items-1.title"),
      desc: t("menu.learn.revolutionizing.items-1.desc"),
      iconPath: t("/learn/inbody-tech/technology_03.webp"),
    },
    {
      id: 2,
      title: t("menu.learn.revolutionizing.items-2.title"),
      desc: t("menu.learn.revolutionizing.items-2.desc"),
      iconPath: t("/learn/inbody-tech/technology_04.webp"),
    },
    {
      id: 3,
      title: t("menu.learn.revolutionizing.items-3.title"),
      desc: t("menu.learn.revolutionizing.items-3.desc"),
      iconPath: t("/learn/inbody-tech/technology_01.webp"),
    },
    {
      id: 4,
      title: t("menu.learn.revolutionizing.items-4.title"),
      desc: t("menu.learn.revolutionizing.items-4.desc"),
      iconPath: t("/learn/inbody-tech/technology_02.webp"),
    },
  ];
  const ComparisonDatas: TResultSheet[] = [
    {
      id: 1,
      category: t("menu.learn.comparison.category-1"),
      title: t("menu.learn.comparison.category-1.title-1"),
      desc_1: t("menu.learn.comparison.category-1.desc-1"),
      desc_2: t("menu.learn.comparison.category-1.desc-2"),
    },
    {
      id: 2,
      category: t("menu.learn.comparison.category-2"),
      title: t("menu.learn.comparison.category-2.title-1"),
      desc_1: t("menu.learn.comparison.category-2.desc-1"),
      desc_2: t("menu.learn.comparison.category-2.desc-2"),
    },
    {
      id: 3,
      category: t("menu.learn.comparison.category-3"),
      title: t("menu.learn.comparison.category-3.title-1"),
      desc_1: t("menu.learn.comparison.category-3.desc-1"),
      desc_2: t("menu.learn.comparison.category-3.desc-2"),
    },
    {
      id: 4,
      category: t("menu.learn.comparison.category-4"),
      title: t("menu.learn.comparison.category-4.title-1"),
      desc_1: t("menu.learn.comparison.category-4.desc-1"),
      desc_2: t("menu.learn.comparison.category-4.desc-2"),
    },
  ];
  return (
    <>
      <Header />
      <Banner
        Bg="/main/header/super-high-1030x634.jpg"
        Headline={t("gnb.learn")}
        Title={t("menu.learn.inbody-technology")}
        Desc={t("learn.inbody-technology.desc")}
      />
      <TitleBannerWrapper>
        <TitleBannerTitle
          dangerouslySetInnerHTML={{ __html: t("menu.learn.inbody-technology.what-is-bia-tech-in-inbody.title") }}
        />
        <TitleBannerDesc>{t("menu.learn.inbody-technology.bia-technology-in-inbody.desc")}</TitleBannerDesc>
      </TitleBannerWrapper>
      <Timeline tab="inbodyTech">{t("learn.inbody-technology.history-of-bia-technology")}</Timeline>
      <RevolutionWrapper>
        <RevolutionTitleWrapper>
          <RevolutionTitle>{t("menu.learn.revolutionizing-bia-tech")}</RevolutionTitle>
          <RevolutionDesc>{t("menu.learn.revolutionizing-bia-tech.desc")}</RevolutionDesc>
        </RevolutionTitleWrapper>
        <RevolutionContentWrapper>
          {RevolutionDatas.map((data) => {
            return (
              <RevolutionItem key={data.id}>
                <RevolutionItemTitle
                  data-aos="fade-up"
                  data-aos-easing="ease-out"
                  data-aos-delay="200"
                  data-aos-duration="500"
                >
                  {data.title}
                </RevolutionItemTitle>
                <Img src={data.iconPath} alt="inbody-tech" />
                <P $fontColor={theme.colors.text.grayLight} $lineHeight="150%">
                  {data.desc}
                </P>
              </RevolutionItem>
            );
          })}
        </RevolutionContentWrapper>
      </RevolutionWrapper>
      <ComparisonWrapper>
        <ComparisonTitle>{t("menu.learn.comparison.title")}</ComparisonTitle>
        <ResultSheet $contentHeight="420px" datas={ComparisonDatas} />
      </ComparisonWrapper>
      <TechTrustWrapper>
        <TechTrustContentWrapper>
          <TechTrustImg />
          <TechTrustContent>
            <TechTrustContentTitle>{t("menu.learn.technology-trust.title")}</TechTrustContentTitle>
            <TechTrustContentDesc dangerouslySetInnerHTML={{ __html: t("menu.learn.technology-trust.desc") }} />
            <Button href="/learn/inbody-in-studies/">{t("menu.learn.technology-trust.button")}</Button>
          </TechTrustContent>
        </TechTrustContentWrapper>
      </TechTrustWrapper>
      <Footer />
    </>
  );
}

const TitleBannerWrapper = styled.div`
  width: 100%;
  min-height: 188px;
  display: flex;
  padding: 0 100px;
  justify-content: center;
  align-items: center;
  ${media.desktopMid`
    min-height: 126px;
    margin: 50px 0 20px 0;
    padding: 0 100px;
    flex-wrap: wrap;
    flex-direction: row;
    gap: 10px;
    align-items: normal;
  `}
  ${media.mobile`
     padding: 0 20px;
  `}
`;
const TitleBannerTitle = styled.p`
  width: 560px;
  min-height: 88px;
  font-size: 2.8rem;
  font-weight: ${theme.fontWeights.bold};
  white-space: pre-wrap;
  ${media.desktopMid`
    width: 100%;
    min-height: 44px;
  `}
  ${media.mobile`
      font-size: 2.3rem;
  `}
`;
const TitleBannerDesc = styled.p`
  width: 1121px;
  min-height: 88px;
  line-height: 150%;
  color: ${theme.colors.text.grayLight};
  ${media.desktopMid`
      width: 100%;
      min-height: 44px;
  `}
`;

const RevolutionWrapper = styled.div`
  width: 100%;
  padding: 0 20px;
  background: linear-gradient(
    ${theme.colors.background.white},
    ${theme.colors.background.default},
    ${theme.colors.background.default}
  );
`;

const RevolutionTitleWrapper = styled.div`
  width: 100%;
  min-height: 212px;
  height: auto;
  display: flex;
  align-items: center;
  padding: 0 100px;
  ${media.desktopMid`
    flex-direction: column;
    min-height: 225px;
    justify-content: center;
  `}
  ${media.phablet`
    padding: 0;  
  `}
`;
const RevolutionTitle = styled.div`
  width: 34%;
  min-height: 113px;
  height: auto;
  margin-bottom: 10px;
  font-size: 2.8rem;
  font-weight: ${theme.fontWeights.bold};
  white-space: pre-line;
  ${media.desktopMid`
    width: 100%;
    min-height: 59.8px;
    height: auto;
  `}
  ${media.phablet`
    text-align: center;  
  `}
  ${media.mini`
    font-size: 2rem;  
  `}
`;
const RevolutionDesc = styled.div`
  width: 73%;
  min-height: 113px;
  line-height: 150%;
  color: ${theme.colors.text.grayLight};
  ${media.desktopMid`
    width: 100%;
    min-height: 50px;
    marigin-top: 10px;
  `}
`;
const RevolutionContentWrapper = styled.div`
  width: 100%;
  height: 620px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 20px;
  ${media.tablet`
    flex-wrap: wrap;
    height: auto;
    justify-content: center;
  `}
`;

const RevolutionItem = styled.div`
  width: 445px;
  height: 603px;
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  padding: 42px 32px;
  border-radius: 10px;
  background-color: ${theme.colors.background.white};
  ${media.tablet`
    width: 48%;
    margin-bottom: 0;
  `}
  ${media.phablet`
    width: 100%;
  `}
`;
const RevolutionItemTitle = styled.p`
  font-size: 2rem;
  font-weight: ${theme.fontWeights.bold};
  ${media.tabletWide`
      font-size: 1.7rem;
  `}
  ${media.tablet`
    font-size: 2rem;
  `}
`;

const P = styled.p<IFontProps>`
  width: 100%;
  font-weight: ${(props) => props.$fontWeight};
  font-size: ${(props) => props.$fontSize};
  line-height: ${(props) => props.$lineHeight};
  text-align: ${(props) => props.$textAlign};
  padding: ${(props) => props.$padding};
  white-space: pre-wrap;
  color: ${(props) => props.$fontColor};
  ${media.tablet`
    &#techTrustTitle {
      font-size: 2rem;
    }
  `}
`;

const Img = styled.img`
  width: 100%;
  height: auto;
`;

const ComparisonWrapper = styled.div`
  width: 100%;
  background-color: ${theme.colors.background.white};
  ${media.tablet`
    height: 100%;
  `}
`;
const ComparisonTitle = styled.p`
  width: 100%;
  margin: auto;
  padding: 25px;
  line-height: 130%;
  text-align: center;
  font-weight: ${theme.fontWeights.bold};
  font-size: 2.8rem;
  white-space: pre-wrap;
`;

const TechTrustWrapper = styled.div`
  width: 100%;
  min-height: 516px;
  display: flex;
  margin-top: -220px;
  padding: 0 10px;
  overflow: hidden;
  ${media.desktop`
    heihgt: auto;
  `}
  ${media.tablet`
    margin-top: 30px;
  `}
`;
const TechTrustContentWrapper = styled.div`
  width: 100%;
  min-height: 440px;
  display: flex;
  flex-direction: row;
  gap: 25px;
  padding: 0 20px;
  overflow: hidden;
  ${media.desktop`
     height: auto;
  `}
`;
const TechTrustImg = styled.div`
  width: 50%;
  height: 424px;
  border-radius: 10px;
  background-size: cover;
  background-position: center;
  background: url("/learn/inbody-tech/Guiding-Principles.png") no-repeat;
  ${media.desktop`
    display: none;  
  `}
`;

const TechTrustContent = styled.div`
  width: 50%;
  height: 424px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 25px;
  padding: 95px 55px;
  margin-bottom: 16px;
  border-radius: 10px;
  background-color: ${theme.colors.background.white};
  ${media.desktop`
     width: 100%;
  `}
  ${media.mobileWide`    
     height: auto;
  `}
`;
const TechTrustContentTitle = styled.p`
  font-weight: ${theme.fontWeights.bold};
  font-size: 2.8rem;
  text-align: center;
  color: ${theme.colors.primary};
`;
const TechTrustContentDesc = styled.p`
  line-height: 150%;
  text-align: center;
  color: ${theme.colors.text.grayLight};
`;

const Button = styled(Link)`
  width: 200px;
  height: 40px;
  display: flex;
  padding: 10px;
  border-radius: 20px;
  flex-direction: column;
  align-items: center;
  border: 1px solid black;
  cursor: pointer;
  transition: 0.3s ease-out;
  background-color: ${theme.colors.button.black};
  color: ${theme.colors.text.white};
  &:hover {
    background-color: ${theme.colors.background.white};
    color: ${theme.colors.text.default};
    transition: 0.3s ease-out;
  }
`;
